/* eslint-disable @typescript-eslint/ban-types */
import { Subject } from 'rxjs';

declare global {
	interface String {
		remove(str: string): string;
		stripHtml(): string;
		hashCode(): string;
	}

	interface Array<T> {
		any(predicate?: (value: T, index: number, object: T[]) => boolean): boolean;
		compare(items: Array<T>): Array<T>;
		first(): T;
		last(): T;
		remove(predicate: (value: T) => boolean): void;
		remove(item: T): void;
		distinct(): Array<T>;
	}

	interface Function {
		delay<T>(bind: T, milliseconds: number, ...args: unknown[]): number;
		delay(milliseconds: number, ...args: unknown[]): number;
		periodical<T>(bind: T, milliseconds: number, ...args: unknown[]): number;
		periodical(milliseconds: number, ...args: unknown[]): number;
		pass<T>(bind: T, ...args: unknown[]): Function;
		pass(...args: unknown[]): Function;
	}

	interface Object {
		getSubject<T>(obj: Record<string, unknown>, prop: string): Subject<T>;
	}

	interface HTMLElement {
		scrollTo(options?: ScrollToOptions): void;
		scrollTo(x: number, y: number): void;
	}
}

/** string extensions */

/** remove the specified string from the given string */
String.prototype.remove = function (str: string): string {
	if (this.includes(str)) {
		return this.replace(str, '');
	}
	return this;
};

/** strip html */
String.prototype.stripHtml = function (): string {
	const tmp: HTMLDivElement = document.createElement('div');
	tmp.innerHTML = this;
	return tmp.textContent || tmp.innerText || '';
};

/** create a hashCode from the given string */
/** From: https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript */
String.prototype.hashCode = function () {
	// tslint:disable-next-line:no-bitwise
	return this.split('').reduce(function (a, b) {
		a = (a << 5) - a + b.charCodeAt(0);
		return a & a;
	}, 0);
};

/** array extensions */

/** find whether there are any elements in the array that match the predicate */
Array.prototype.any = function <T>(predicate?: (value: T, index: number, object: T[]) => boolean): boolean {
	if (!predicate) {
		return this.length > 0;
	}
	if ((this as Array<T>).find(predicate)) {
		return true;
	}
	return false;
};

/** compare array to another array and return missing values */
Array.prototype.compare = function <T>(items: Array<T>): Array<T> {
	const missingValues: Array<T> = [];
	for (let i: number = 0; i < this.length; i++) {
		if (!items.includes(this[i])) {
			missingValues.push(this[i]);
		}
	}
	return missingValues;
};

/** return the first element */
Array.prototype.first = function <T>(): T {
	if (this === undefined || this === null || this.length === 0) {
		return null;
	} else {
		return this[0];
	}
};

/** return the last element */
Array.prototype.last = function <T>(): T {
	if (this === undefined || this === null || this.length === 0) {
		return null;
	} else {
		return this[this.length - 1];
	}
};

/** remove duplicate values from an array */
Array.prototype.distinct = function <T>(): Array<T> {
	return this.filter((value: T, index: number, self: Array<T>) => self.indexOf(value) === index);
};

/** remove an entry from an array */
function remove<T>(predicate: (value: T) => boolean): void;
function remove<T>(item: T): void;
function remove<T>(x: T | ((value: T) => boolean)): void {
	if (typeof x === 'function') {
		const array: T[] = this;
		array.forEach((item, i) => {
			if (x.call(this, item) === true) {
				array.splice(i, 1);
			}
		});
	} else {
		if (this.includes(x)) {
			this.splice(this.indexOf(x), 1);
		}
	}
}
Array.prototype.remove = remove;

/** function extensions */

/** delay a function execution */
function delay<T>(bind: T, milliseconds: number, ...args: unknown[]): number;
function delay(milliseconds: number, ...args: unknown[]): number;
function delay<T>(x: T, y: number | unknown[], z?: unknown[]): number {
	let bind: T = null;
	let milliseconds: number = 0;
	let args: unknown[] = [];

	if (typeof x !== 'number') {
		bind = x;
		milliseconds = y as number;

		if (z) {
			args = z;
		}
	} else {
		milliseconds = x;

		if (y) {
			args = y as unknown[];
		}
	}

	return setTimeout((this as Function).pass(bind, ...args), milliseconds);
}
Function.prototype.delay = delay;

/** execute a function on the given interval (milliseconds) */
function periodical<T>(bind: T, milliseconds: number, ...args: unknown[]): number;
function periodical(milliseconds: number, ...args: unknown[]): number;
function periodical<T>(x: T, y: number | unknown[], z?: unknown[]): number {
	let bind: T = null;
	let milliseconds: number = 0;
	let args: unknown[] = [];

	if (typeof x !== 'number') {
		bind = x;
		milliseconds = y as number;

		if (z) {
			args = z;
		}
	} else {
		milliseconds = x;

		if (y) {
			args = y as unknown[];
		}
	}

	return setInterval((this as Function).pass(bind, ...args), milliseconds);
}
Function.prototype.periodical = periodical;

/** create a new function reference with bound arguments */
function pass<T>(bind: T, ...args: unknown[]): Function;
function pass(...args: unknown[]): Function;
function pass<T>(x: T | unknown[], y: unknown[]): Function {
	const self: Function = this;
	let bind: T = null;
	let args: unknown[] = [];

	if (x instanceof Array) {
		args = x;
	} else {
		bind = x;

		if (y) {
			args = y;
		}
	}

	return function () {
		return self.call(bind, ...args);
	};
}
Function.prototype.pass = pass;

/** object extensions */

/** subscribe to the values of an objects property */
function getSubject<T>(obj: Record<string, unknown>, prop: string): Subject<T> {
	/** setup a subject observable */
	const valueSubject: Subject<T> = new Subject<T>();

	/** get the original value */
	let propValue: T = obj[prop] as T;

	/** create the getter */
	const getter = (): T => propValue;

	/** create the setter */
	const setter = (value: T): void => {
		/** set the property value */
		propValue = value;

		/** broadcast the new value */
		valueSubject.next(propValue);
	};

	/** define the property on the object */
	Object.defineProperty(obj, prop, { get: getter, set: setter });

	/** return the subject */
	return valueSubject;
}
Object.getSubject = getSubject;

/** example of getSubject
const testObj: { key1: number, key2: string } = { key1: 10, key2: 'Test string' };
const valueChange: Subject<number> = Object.getSubject<number>(testObj, 'key1');

valueChange.subscribe(val => console.log(val));

let i = 0;
window.setInterval(() => {
	testObj.key1 = i;
	i++;
}, 1000);
*/

/** HTMLElement extensions */

/** scroll the window for Edge */
function scrollTo(options?: ScrollToOptions): void;
function scrollTo(x: number, y: number): void;
function scrollTo(x?: ScrollToOptions | number, y?: number): void {
	if (typeof x === 'number') {
		this.scrollLeft = x;
		this.scrollTop = y;
	} else if (x !== null) {
		this.scrollLeft = x.left;
		this.scrollTop = x.top;
	}
}

/** check for missing scroll to */
if (!HTMLElement.prototype.scrollTo) {
	HTMLElement.prototype.scrollTo = scrollTo;
}

export {};
